import { RefObject, useEffect, useMemo, useRef, useState } from 'react';

import { getLocalStorage } from '@/utils/localstorage';

import {
	applyMarketoFormLocalization,
	type MarketoFormLocalization,
	observeMarketoValidationLocalization,
} from './marketo-form-localization';

export type MarketoFormStatus = 'loading' | 'form' | 'success' | 'error';

export type MarketoFormIds = {
	formId: number | string;
	formIdStg?: number | string | null;
};

export type MarketoFormConfig = {
	scriptHost: string;
	preferencesUrl: string;
	orgId: string;
	formId: number;
};

type MarketoForm = {
	render: () => HTMLElement;
	onSubmit: (callback: () => void) => void;
	onSuccess: (callback: () => boolean) => void;
	vals: (values: Record<string, string>) => void;
	submitable?: (canSubmit: boolean) => void;
	getFormElem?: () => { find?: (selector: string) => { text?: () => string } };
};

type MktoForms2 = {
	loadForm: (
		scriptHost: string,
		orgId: string,
		formId: number,
		callback: (form?: MarketoForm) => void,
	) => void;
};

type MarketoPreferences = {
	locale?: string;
	localeRequiresMarketingCommunicationOptIn?: boolean;
};

declare global {
	interface Window {
		MktoForms2?: MktoForms2;
	}
}

export const PROD_MARKETO_SCRIPT_HOST = 'https://pages.eml.atlassian.com';
export const STAGING_MARKETO_SCRIPT_HOST = 'https://app-ab35.marketo.com';
export const PROD_MARKETO_ORG_ID = '594-ATC-127';
export const STAGING_MARKETO_ORG_ID = '353-DGC-691';
export const PROD_PREFERENCES_URL = 'https://preferences.atlassian.com/rest/forms/config';
export const STAGING_PREFERENCES_URL = 'https://preferences.stg.atlassian.com/rest/forms/config';

const MARKETING_CONTEXT_STORAGE_KEY = 'marketingContext';
const MARKETO_READY_POLL_MS = 250;
const MARKETO_READY_TIMEOUT_MS = 15000;
const MARKETO_LOAD_FORM_TIMEOUT_MS = 15000;
const MARKETO_SUBMIT_TIMEOUT_MS = 15000;

const toFormId = (formId: number | string): number => Number(formId);
const getMarketoFormElementId = (formId: number): string => `mktoForm_${formId}`;
const getMarketoScriptKey = (scriptHost: string, orgId: string): string => `${scriptHost}:${orgId}`;
const getMarketoScriptId = (scriptHost: string, orgId: string): string =>
	`marketo-${orgId}-${scriptHost.replace(/[^a-zA-Z0-9]/g, '-')}`;

const loadedMarketoInstances = new Map<string, MktoForms2>();
const loadingMarketoScripts = new Map<string, Promise<MktoForms2>>();

export const getMarketoConfig = (ids: MarketoFormIds, isProduction: boolean): MarketoFormConfig => {
	const hasStagingFormId =
		ids.formIdStg !== undefined && ids.formIdStg !== null && ids.formIdStg !== '';
	const shouldUseProductionForm = isProduction || !hasStagingFormId;
	const selectedFormId = shouldUseProductionForm ? ids.formId : ids.formIdStg;

	return {
		scriptHost: shouldUseProductionForm ? PROD_MARKETO_SCRIPT_HOST : STAGING_MARKETO_SCRIPT_HOST,
		preferencesUrl: shouldUseProductionForm ? PROD_PREFERENCES_URL : STAGING_PREFERENCES_URL,
		orgId: shouldUseProductionForm ? PROD_MARKETO_ORG_ID : STAGING_MARKETO_ORG_ID,
		formId: toFormId(selectedFormId ?? ids.formId),
	};
};

const waitForMarketo = (
	scriptElement: HTMLScriptElement,
	scriptKey: string,
): Promise<MktoForms2> => {
	return new Promise((resolve, reject) => {
		const cleanup = () => {
			scriptElement.removeEventListener('load', handleLoad);
			scriptElement.removeEventListener('error', handleError);
			window.clearInterval(pollId);
			window.clearTimeout(timeoutId);
		};

		function handleLoad() {
			scriptElement.dataset['marketoLoaded'] = 'true';
			updateMarketo();
		}

		function updateMarketo() {
			if (scriptElement.dataset['marketoLoaded'] === 'true' && window.MktoForms2) {
				const marketo = window.MktoForms2;
				loadedMarketoInstances.set(scriptKey, marketo);
				cleanup();
				resolve(marketo);
			}
		}

		function handleError() {
			cleanup();
			scriptElement.remove();
			loadedMarketoInstances.delete(scriptKey);
			reject(new Error('Failed to load Marketo.'));
		}

		scriptElement.addEventListener('load', handleLoad);
		scriptElement.addEventListener('error', handleError);
		const pollId = window.setInterval(updateMarketo, MARKETO_READY_POLL_MS);
		const timeoutId = window.setTimeout(() => {
			cleanup();
			scriptElement.remove();
			loadedMarketoInstances.delete(scriptKey);
			reject(new Error('Timed out waiting for Marketo.'));
		}, MARKETO_READY_TIMEOUT_MS);

		updateMarketo();
	});
};

const loadMarketoScript = (scriptHost: string, orgId: string): Promise<MktoForms2> => {
	if (typeof window === 'undefined' || typeof document === 'undefined') {
		return Promise.reject(new Error('Marketo forms can only load in the browser.'));
	}

	const scriptKey = getMarketoScriptKey(scriptHost, orgId);
	const scriptId = getMarketoScriptId(scriptHost, orgId);
	const existingScript = document.getElementById(scriptId) as HTMLScriptElement | null;
	const cachedInstance = loadedMarketoInstances.get(scriptKey);

	if (cachedInstance && existingScript) {
		return Promise.resolve(cachedInstance);
	}

	if (
		!existingScript &&
		window.MktoForms2 &&
		!document.querySelector('script[data-marketo-script="true"], script[id^="marketo-"]')
	) {
		return Promise.resolve(window.MktoForms2);
	}

	const pendingScript = loadingMarketoScripts.get(scriptKey);
	if (pendingScript && existingScript) {
		return pendingScript;
	}

	if (existingScript) {
		const loadPromise = waitForMarketo(existingScript, scriptKey).finally(() => {
			loadingMarketoScripts.delete(scriptKey);
		});
		loadingMarketoScripts.set(scriptKey, loadPromise);
		return loadPromise;
	}

	const script = document.createElement('script');
	script.id = scriptId;
	script.async = true;
	script.src = `${scriptHost}/js/forms2/js/forms2.min.js`;
	script.dataset['marketoScript'] = 'true';
	script.dataset['marketoKey'] = scriptKey;
	document.body.appendChild(script);

	const loadPromise = waitForMarketo(script, scriptKey).finally(() => {
		loadingMarketoScripts.delete(scriptKey);
	});
	loadingMarketoScripts.set(scriptKey, loadPromise);
	return loadPromise;
};

const getSubmitButtonText = (form: MarketoForm): string => {
	return form.getFormElem?.().find?.('button[type="submit"]')?.text?.() ?? '';
};

const getRenderedSubmitButton = (formElement: HTMLElement): HTMLButtonElement | null =>
	formElement.querySelector<HTMLButtonElement>('button[type="submit"], .mktoButton');

const getRenderedSubmitButtonText = (formElement: HTMLElement): string =>
	getRenderedSubmitButton(formElement)?.textContent?.trim() ?? '';

const restoreSubmitButton = (formElement: HTMLElement, submitButtonText: string) => {
	const submitButton = getRenderedSubmitButton(formElement);

	if (!submitButton) {
		return;
	}

	if (submitButtonText) {
		submitButton.textContent = submitButtonText;
	}

	submitButton.disabled = false;
	submitButton.removeAttribute('disabled');
};

const hasMarketoValidationError = (formElement: HTMLElement): boolean =>
	Boolean(formElement.querySelector('.mktoInvalid, .mktoError'));

const getVisibleLabelText = (element: Element): string =>
	(element.textContent ?? '').replace(/\*/g, '').trim();

const normalizeMarketoMarkup = (formElement: HTMLElement) => {
	document
		.querySelectorAll('#mktoForms2BaseStyle, #mktoForms2ThemeStyle')
		.forEach((element) => element.remove());
	formElement
		.querySelectorAll('style, .mktoGutter, .mktoClear, .mktoOffset')
		.forEach((element) => element.remove());

	formElement.removeAttribute('style');
	formElement.querySelectorAll<HTMLElement>('*').forEach((element) => {
		element.removeAttribute('style');
	});

	const submitText = formElement.getAttribute('data-submit');
	if (submitText) {
		formElement.querySelectorAll('button').forEach((button) => {
			button.textContent = submitText;
		});
	}

	formElement.querySelectorAll('.mktoLabel').forEach((element) => {
		element.classList.add('body-sm', 'bold');

		const requiredAsterisk = element.querySelector('.mktoAsterix');
		if (requiredAsterisk) {
			element.appendChild(requiredAsterisk);
		}
	});

	formElement.querySelectorAll<HTMLElement>('.mktoFieldWrap').forEach((fieldWrap) => {
		const checkboxList = fieldWrap.querySelector<HTMLElement>('.mktoCheckboxList');
		if (!checkboxList) {
			return;
		}

		const fieldLabel = Array.from(fieldWrap.children).find(
			(element): element is HTMLElement =>
				element instanceof HTMLElement && element.classList.contains('mktoLabel'),
		);

		if (!fieldLabel || !getVisibleLabelText(fieldLabel)) {
			return;
		}

		const optionLabels = Array.from(checkboxList.querySelectorAll<HTMLLabelElement>('label'));
		const hasVisibleOptionLabel = optionLabels.some((label) => getVisibleLabelText(label));

		if (hasVisibleOptionLabel) {
			return;
		}

		optionLabels.forEach((label) => label.remove());
		fieldLabel.classList.remove('body-sm', 'bold');
		fieldLabel.classList.add('body-md');
		checkboxList.appendChild(fieldLabel);
	});

	formElement
		.querySelectorAll('input, select, textarea, button, .mktoHtmlText')
		.forEach((element) => {
			element.classList.add('body-md');
		});
	formElement
		.querySelectorAll('.mktoCheckboxList label, .mktoRadioList label')
		.forEach((element) => {
			element.classList.add('body-md');
		});
	formElement.querySelectorAll('.mktoInstruction, .mktoErrorMsg').forEach((element) => {
		element.classList.add('fine-print');
	});
};

const prepareMarketoFormTarget = (container: HTMLElement, formId: number) => {
	container.innerHTML = '';

	const form = document.createElement('form');
	form.id = getMarketoFormElementId(formId);
	container.appendChild(form);
};

const getRenderedMarketoForm = (container: HTMLElement, form: MarketoForm, formId: number) => {
	const targetForm = container.querySelector<HTMLElement>(`#${getMarketoFormElementId(formId)}`);

	if (targetForm && targetForm.children.length > 0) {
		return targetForm;
	}

	return form.render();
};

export const applyMarketoPreferences = async (
	formElement: HTMLElement,
	preferencesUrl: string,
): Promise<void> => {
	if (typeof window === 'undefined' || typeof window.fetch !== 'function') {
		return;
	}

	try {
		const response = await window.fetch(preferencesUrl);
		if (!response.ok) {
			throw new Error(`Failed to fetch Marketo preferences: ${response.status}`);
		}

		const data = (await response.json()) as MarketoPreferences;
		const country = formElement.querySelector<HTMLSelectElement>('select#Country');
		if (country && data.locale) {
			country.closest<HTMLElement>('.mktoFormRow')?.setAttribute('hidden', '');
			country.value = data.locale;
		}

		const gdprInput = formElement.querySelector<HTMLInputElement>('input[name="form_gmoiGranted"]');
		if (gdprInput && data.localeRequiresMarketingCommunicationOptIn === false) {
			gdprInput.closest<HTMLElement>('.mktoFormRow')?.setAttribute('hidden', '');
			gdprInput.checked = true;
		}
	} catch (error) {
		console.error('Failed to retrieve Marketo preferences', error);
	}
};

export const getUtmHiddenFields = (): Record<string, string> => {
	try {
		const marketingContext = getLocalStorage(MARKETING_CONTEXT_STORAGE_KEY, true);
		const parsedMarketingContext = marketingContext ? JSON.parse(marketingContext) : null;
		const {
			sfdcCampaignId = '',
			source = '',
			medium = '',
			campaign = '',
			content = '',
		} = parsedMarketingContext?.utm || {};

		return {
			UTM_SFDC_Campaign_ID__c: sfdcCampaignId,
			utmSource: source,
			utmMedium: medium,
			utmCampaign: campaign,
			utmContent: content,
		};
	} catch (error) {
		console.error('Failed to read or parse marketingContext from localStorage', error);
		return {};
	}
};

export const getMarketoHiddenFields = (
	form: MarketoForm,
	submitButtonText = getSubmitButtonText(form),
): Record<string, string> => {
	const timestamp = new Date().toISOString();
	const currentUrl = typeof window === 'undefined' ? '' : window.location.href;

	return {
		form_anyConsentUpdatedAt: timestamp,
		form_gmoiUpdatedAt: timestamp,
		form_privacyPolicyUpdatedAt: timestamp,
		form_gmoiFormUrl: currentUrl,
		form_privacyPolicyFormUrl: currentUrl,
		form_submitButton: submitButtonText,
		...getUtmHiddenFields(),
	};
};

type UseMarketoFormArgs = {
	containerRef: RefObject<HTMLDivElement>;
	config: MarketoFormConfig;
	localization?: MarketoFormLocalization;
};

export const useMarketoForm = ({ containerRef, config, localization }: UseMarketoFormArgs) => {
	const [status, setStatus] = useState<MarketoFormStatus>('loading');
	const activeConfigKeyRef = useRef<string>();
	const { scriptHost, preferencesUrl, orgId, formId } = config;

	const configKey = useMemo(
		() => `${scriptHost}:${preferencesUrl}:${orgId}:${formId}`,
		[formId, orgId, preferencesUrl, scriptHost],
	);

	useEffect(() => {
		let isMounted = true;
		let submitTimeoutId: number | undefined;
		let validationLocalizationObserver: MutationObserver | undefined;
		const loadTimeoutId = window.setTimeout(() => {
			if (isMounted) {
				setStatus('error');
			}
		}, MARKETO_LOAD_FORM_TIMEOUT_MS);

		const clearSubmitTimeout = () => {
			if (submitTimeoutId) {
				window.clearTimeout(submitTimeoutId);
				submitTimeoutId = undefined;
			}
		};

		const setLoadedStatus = (nextStatus: MarketoFormStatus) => {
			window.clearTimeout(loadTimeoutId);
			clearSubmitTimeout();
			if (isMounted) {
				setStatus(nextStatus);
			}
		};

		if (activeConfigKeyRef.current !== configKey) {
			activeConfigKeyRef.current = configKey;
			setStatus('loading');
		}

		loadMarketoScript(scriptHost, orgId)
			.then((marketo) => {
				if (!isMounted || !containerRef.current) {
					return;
				}

				try {
					prepareMarketoFormTarget(containerRef.current, formId);
					marketo.loadForm(scriptHost, orgId, formId, (form) => {
						if (!isMounted || !containerRef.current) {
							return;
						}

						if (!form) {
							setLoadedStatus('error');
							return;
						}

						const renderedForm = getRenderedMarketoForm(containerRef.current, form, formId);
						normalizeMarketoMarkup(renderedForm);
						if (localization) {
							applyMarketoFormLocalization(renderedForm, localization);
							validationLocalizationObserver = observeMarketoValidationLocalization(
								renderedForm,
								localization,
							);
						}
						const submitButtonText =
							getRenderedSubmitButtonText(renderedForm) || getSubmitButtonText(form);

						if (renderedForm.parentElement !== containerRef.current) {
							containerRef.current.innerHTML = '';
							containerRef.current.appendChild(renderedForm);
						}

						form.onSubmit(() => {
							clearSubmitTimeout();

							try {
								form.vals(getMarketoHiddenFields(form, submitButtonText));
								submitTimeoutId = window.setTimeout(() => {
									if (!isMounted) {
										return;
									}

									form.submitable?.(true);
									restoreSubmitButton(renderedForm, submitButtonText);

									if (!hasMarketoValidationError(renderedForm)) {
										setLoadedStatus('error');
									}
								}, MARKETO_SUBMIT_TIMEOUT_MS);
							} catch (error) {
								console.error('Failed to prepare Marketo submission', error);
								form.submitable?.(true);
								restoreSubmitButton(renderedForm, submitButtonText);
								setLoadedStatus('error');
							}
						});

						form.onSuccess(() => {
							clearSubmitTimeout();
							setLoadedStatus('success');
							return false;
						});

						setLoadedStatus('form');
						window.setTimeout(() => {
							if (isMounted) {
								void applyMarketoPreferences(renderedForm, preferencesUrl);
							}
						}, 0);
					});
				} catch {
					setLoadedStatus('error');
				}
			})
			.catch(() => {
				setLoadedStatus('error');
			});

		return () => {
			isMounted = false;
			window.clearTimeout(loadTimeoutId);
			clearSubmitTimeout();
			validationLocalizationObserver?.disconnect();
		};
	}, [configKey, containerRef, formId, localization, orgId, preferencesUrl, scriptHost]);

	return status;
};
